import React, { useCallback, useEffect, useRef, useState, useContext } from "react";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Separator } from "../../components/ui/separator";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Brain,
  Send,
  Search,
  Clock,
  BarChart3,
  MessageCircle,
  Users,
  PhoneCall,
  User,
  TrendingUp,
  Loader2,
  AlertTriangle,
  ArrowUpRight,
  Calendar,
  Printer,
  FileText,
  RefreshCw,
  CheckCircle2,
  Sparkles,
  Lightbulb,
  ArrowRight,
  Download,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const SUGESTOES_BASE = [
  {
    rotulo: "Atendimentos hoje",
    pergunta: "Quantos tickets foram abertos hoje?",
    icone: Search,
    tipo: "padrao",
  },
  {
    rotulo: "SLA",
    pergunta: "Quais atendimentos estão fora do SLA agora?",
    icone: Clock,
    tipo: "sla",
  },
  {
    rotulo: "Atendentes",
    pergunta: "Quanto tempo os atendentes levam para responder?",
    icone: Users,
    tipo: "atendentes",
  },
  {
    rotulo: "Canais",
    pergunta: "Qual canal gera mais atendimentos este mês?",
    icone: PhoneCall,
    tipo: "canais",
  },
  {
    rotulo: "Clientes",
    pergunta: "Quais clientes estão sem resposta há mais de 2 horas?",
    icone: User,
    tipo: "semresposta",
  },
  {
    rotulo: "Tendências",
    pergunta: "Mostre a evolução dos atendimentos nos últimos 30 dias.",
    icone: TrendingUp,
    tipo: "tendencia",
  },
];

function hashStr6(str) {
  if (!str) return "000000";
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h.toString(36).padStart(6, "0").slice(-6).toUpperCase();
}

function numeroRelatorio(periodLabel) {
  const hoje = new Date();
  const a = String(hoje.getFullYear());
  const m = String(hoje.getMonth() + 1).padStart(2, "0");
  const d = String(hoje.getDate()).padStart(2, "0");
  const hh = String(hoje.getHours()).padStart(2, "0");
  const mm = String(hoje.getMinutes()).padStart(2, "0");
  const sigla = (periodLabel || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^A-Za-z0-9]/g, "").slice(0, 4).toUpperCase() || "REL";
  const seq = String(Math.floor(Math.random() * 900 + 100));
  return `PI-${a}${m}${d}-${hh}${mm}-${sigla}-${seq}`;
}

function tabelaParaCsv(tabela) {
  if (!tabela?.columns?.length || !tabela?.rows?.length) return "";
  const escapar = (v) => {
    const s = v == null ? "" : String(v);
    if (/[",;\n\r\t]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const header = tabela.columns.map((c) => escapar(c.label || c.key)).join(";");
  const lines = tabela.rows.map((row) =>
    tabela.columns.map((c) => {
      const raw = row[c.key];
      const valor =
        typeof raw === "object" && raw?.label != null ? raw.label : raw;
      return escapar(valor);
    }).join(";")
  );
  return [header, ...lines].join("\r\n");
}

function baixarArquivo(nome, conteudo, tipo) {
  try {
    const blob = new Blob([conteudo], { type: tipo || "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = nome;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
    return true;
  } catch {
    return false;
  }
}

const COLORS_DEFAULT = [
  "#6366f1",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#f97316",
  "#ec4899",
];
let COLORS = [...COLORS_DEFAULT];

function hslParaHex(hStr, sStr, lStr) {
  const h = Number(hStr) / 360;
  const s = Number(sStr) / 100;
  const l = Number(lStr) / 100;
  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  const paraHex = (x) => {
    const hx = Math.round(x * 255).toString(16);
    return hx.length === 1 ? "0" + hx : hx;
  };
  return `#${paraHex(r)}${paraHex(g)}${paraHex(b)}`;
}

function lerCorCssVarComoHex(varName, fallback) {
  try {
    const root = document.documentElement;
    const raw = getComputedStyle(root).getPropertyValue(varName).trim();
    if (!raw) return fallback;
    const parts = raw.split(/\s+/).map(p => p.trim()).filter(Boolean);
    if (parts.length >= 3) return hslParaHex(parts[0], parts[1], parts[2]);
  } catch {
    // ignore
  }
  return fallback;
}

function ajustarLuminosidade(hex, delta) {
  const c = hex.replace("#", "");
  const num = parseInt(c, 16);
  let r = (num >> 16) & 255;
  let g = (num >> 8) & 255;
  let b = num & 255;
  r = Math.max(0, Math.min(255, r + delta));
  g = Math.max(0, Math.min(255, g + delta));
  b = Math.max(0, Math.min(255, b + delta));
  const paraHex = (x) => {
    const hx = Math.round(x).toString(16);
    return hx.length === 1 ? "0" + hx : hx;
  };
  return `#${paraHex(r)}${paraHex(g)}${paraHex(b)}`;
}

function criarPaletaDoSistema() {
  const primary = lerCorCssVarComoHex("--primary", "#0b63e0");
  const accent = lerCorCssVarComoHex("--accent", "#e0edff");
  return {
    primary,
    primaryClaro: ajustarLuminosidade(primary, 45),
    primaryMuitoClaro: ajustarLuminosidade(primary, 125),
    primaryEscuro: ajustarLuminosidade(primary, -35),
    accent,
    graficos: [
      primary,
      "#10b981",
      "#f59e0b",
      "#ef4444",
      "#8b5cf6",
      "#06b6d4",
      "#f97316",
      "#ec4899",
    ],
  };
}

function formatarDataBr(dt) {
  if (!dt) return "—";
  try {
    const date = typeof dt === "string" ? new Date(dt) : dt;
    if (!Number.isFinite(date.getTime())) return String(dt);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(dt);
  }
}

function formatarDataBrCurta(dt) {
  if (!dt) return "—";
  try {
    const date = typeof dt === "string" ? new Date(dt) : dt;
    if (!Number.isFinite(date.getTime())) return String(dt);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return String(dt);
  }
}

function formatarNumero(v) {
  const n = typeof v === "string" ? Number(v) : v;
  if (!Number.isFinite(n)) return String(v);
  return Intl.NumberFormat("pt-BR").format(n);
}

function formatarDuracaoMin(min) {
  const m = Number(min);
  if (!Number.isFinite(m) || m < 0) return String(min) || "—";
  if (m < 60) return `${m.toFixed(1)} min`;
  const h = Math.floor(m / 60);
  const resto = m % 60;
  if (h < 24) return `${h}h ${resto.toFixed(0)}m`;
  const d = Math.floor(h / 24);
  const hr = h % 24;
  return `${d}d ${hr}h`;
}

function CardKPIS({ items }) {
  if (!items || !items.length) return null;
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 print:grid-cols-3 print:gap-3">
      {items.map((k, idx) => {
        const corBase = k.cor || "var(--intel-primary, #0b63e0)";
        return (
          <Card
            key={idx}
            className={
              "no-break border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md dark:border-slate-700 dark:bg-slate-900 print:shadow-none " +
              "intel-card-kpi"
            }
            style={{ borderLeft: `4px solid ${corBase}` }}
          >
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    {k.label}
                  </p>
                  <p className="mt-1.5 text-2xl font-semibold leading-tight tracking-tight text-slate-900 dark:text-white print:text-xl">
                    {k.value}
                  </p>
                  {k.description ? (
                    <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                      {k.description}
                    </p>
                  ) : null}
                </div>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800 print:h-9 print:w-9">
                  {k.icon ? (
                    <div
                      className="flex h-8 w-8 items-center justify-center rounded-lg print:h-7 print:w-7"
                      style={{
                        background:
                          "color-mix(in srgb, " + corBase + " 14%, transparent)",
                        color: corBase,
                      }}
                    >
                      <k.icon className="h-4 w-4 print:h-3.5 print:w-3.5" />
                    </div>
                  ) : null}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function TabelaDados({ tabela, id, title, accent }) {
  if (!tabela || !tabela.columns || !tabela.rows) return null;
  const corBase = accent || "var(--intel-primary, #0b63e0)";
  return (
    <div
      id={id}
      className="no-break overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900 print:shadow-none"
    >
      {title ? (
        <div
          className="border-b border-slate-200 dark:border-slate-700"
          style={{
            background:
              "color-mix(in srgb, " +
              corBase +
              " 8%, color-mix(in srgb, " +
              corBase +
              " 4%, #f8fafc))",
          }}
        >
          <div className="flex items-center gap-3 px-4 py-3">
            <span
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ background: corBase }}
            />
            <div className="min-w-0 flex-1">
              <p
                className="text-sm font-semibold"
                style={{ color: corBase }}
              >
                {title}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {tabela.rows.length} registros
              </p>
            </div>
          </div>
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow
              className="border-b border-slate-200 dark:border-slate-700"
              style={{
                background:
                  "color-mix(in srgb, " + corBase + " 14%, #ffffff)",
              }}
            >
              {tabela.columns.map((c, i) => (
                <TableHead
                  key={i}
                  className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                  style={{ color: corBase }}
                >
                  {c.label || c.key}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {tabela.rows.slice(0, 500).map((row, i) => (
              <TableRow
                key={i}
                className={
                  "border-b border-slate-100 last:border-0 dark:border-slate-800 " +
                  (i % 2 === 0
                    ? "bg-white dark:bg-slate-900"
                    : "bg-slate-50/40 dark:bg-slate-900/60")
                }
              >
                {tabela.columns.map((c, j) => {
                  const val = row[c.key];
                  return (
                    <TableCell
                      key={j}
                      className="whitespace-normal px-4 py-3 text-sm text-slate-700 dark:text-slate-200"
                    >
                      {typeof val === "object" && val?.href ? (
                        <Link
                          to={val.href}
                          className="no-print-action inline-flex items-center gap-1 font-medium text-primary hover:underline"
                          target={val.target || "_self"}
                          onClick={(e) => {
                            if (window.matchMedia("print").matches)
                              e.preventDefault();
                          }}
                        >
                          {val.label ?? val.href}
                          <ArrowUpRight className="h-3 w-3 print:hidden" />
                        </Link>
                      ) : (
                        <span className="whitespace-pre-wrap">{val}</span>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {tabela.rows.length > 500 ? (
        <p className="px-4 pb-3 pt-1 text-xs text-muted-foreground">
          Mostrando 500 de {tabela.rows.length} resultados.
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 bg-white px-4 py-2.5 dark:border-slate-700 dark:bg-slate-900 print:hidden">
        <p className="flex-1 text-xs text-muted-foreground">
          {tabela.rows.length} registros · {tabela.columns.length} colunas
        </p>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1"
          onClick={() => {
            const csv = tabelaParaCsv(tabela);
            if (!csv) return;
            const nome = `tabela-${(id || title || "dados")
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .replace(/[^A-Za-z0-9-]/g, "-")
              .replace(/-+/g, "-")
              .slice(0, 40)}.csv`;
            baixarArquivo(nome, csv, "text/csv;charset=utf-8");
          }}
        >
          <Download className="h-3.5 w-3.5" />
          Baixar CSV
        </Button>
      </div>
    </div>
  );
}

function Grfico({ grafico, id, title, accent }) {
  if (!grafico || !grafico.type || !grafico.datasets) return null;

  const useTitle = title || grafico.title;
  const { type, labels, datasets } = grafico;
  const corBase = accent || "var(--intel-primary, #0b63e0)";

  const montarRecharts = () => {
    const data = (labels || []).map((label, i) => {
      const pt = { label };
      datasets.forEach((ds, di) => {
        const raw = Array.isArray(ds.data) && ds.data[i] !== undefined ? ds.data[i] : null;
        pt[`d${di}`] = raw == null ? null : Number(raw);
      });
      return pt;
    });

    const pickFill = (ds, i) => {
      if (ds.color) return ds.color;
      if (i === 0) return corBase;
      return COLORS[i % COLORS.length];
    };

    if (type === "bar") {
      return (
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            {datasets.length > 1 ? <Legend /> : null}
            {datasets.map((ds, i) => (
              <Bar
                key={i}
                dataKey={`d${i}`}
                name={ds.label || `Série ${i + 1}`}
                fill={pickFill(ds, i)}
                radius={[6, 6, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (type === "line" || type === "area") {
      return (
        <ResponsiveContainer width="100%" height={320}>
          <LineChart
            data={data}
            margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
          >
            <defs>
              {datasets.map((_, i) => {
              const c = datasets[i]?.color || (i === 0 ? corBase : COLORS[i % COLORS.length]);
              return (
                <linearGradient
                  key={i}
                  id={`gradLine${id}${i}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor={c} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={c} stopOpacity={0} />
                </linearGradient>
              );
            })}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            {datasets.length > 1 ? <Legend /> : null}
            {datasets.map((ds, i) => {
              const c = ds.color || (i === 0 ? corBase : COLORS[i % COLORS.length]);
              return (
                <Line
                  key={i}
                  type="monotone"
                  dataKey={`d${i}`}
                  name={ds.label || `Série ${i + 1}`}
                  stroke={c}
                  fill={type === "area" ? `url(#gradLine${id}${i})` : undefined}
                  strokeWidth={2.5}
                  dot={{ r: 3, strokeWidth: 2 }}
                  activeDot={{ r: 5 }}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      );
    }

    if (type === "pie" || type === "donut") {
      const pieData = (labels || []).map((label, i) => ({
        name: label,
        value:
          Array.isArray(datasets[0]?.data) &&
          datasets[0].data[i] !== undefined
            ? datasets[0].data[i]
            : 0,
      }));
      return (
        <ResponsiveContainer width="100%" height={320}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              innerRadius={type === "donut" ? 80 : 0}
              outerRadius={110}
              paddingAngle={2}
              dataKey="value"
              label={({ name, percent }) =>
                `${name} ${(percent * 100).toFixed(0)}%`
              }
            >
              {pieData.map((_, i) => (
                <Cell key={i} fill={i === 0 ? corBase : COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    return (
      <p className="text-sm text-muted-foreground">
        Tipo de gráfico não suportado: {type}
      </p>
    );
  };

  return (
    <div
      id={id}
      className="no-break space-y-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900 print:shadow-none"
    >
      {useTitle ? (
        <div className="mb-2 flex items-center gap-3">
          <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: corBase }} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">
              {useTitle}
            </p>
            <p className="text-xs text-muted-foreground">
              {labels?.length || 0} categorias
            </p>
          </div>
        </div>
      ) : null}
      {montarRecharts()}
    </div>
  );
}

function Links({ links }) {
  if (!links || !links.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {links.map((l, i) => (
        <Button
          key={i}
          size="sm"
          variant="secondary"
          onClick={() => {
            if (l.href) window.open(l.href, l.target || "_blank");
          }}
        >
          {l.icon ? <l.icon className="mr-1 h-4 w-4" /> : null}
          {l.label}
        </Button>
      ))}
    </div>
  );
}

function transformarRespostaParaUI(raw) {
  const blocos = {
    metrics: [],
    tables: [],
    charts: [],
    links: [],
  };

  if (!raw || !raw.results) return blocos;

  const byChannel = raw.results.find(
    (r) => r.operation === "tickets.byChannel" && Array.isArray(r.data)
  );
  const byAgent = raw.results.find(
    (r) => r.operation === "tickets.byAgent" && Array.isArray(r.data)
  );
  const byQueue = raw.results.find(
    (r) => r.operation === "tickets.byQueue" && Array.isArray(r.data)
  );
  const count = raw.results.find((r) => r.operation === "tickets.count");
  const noResp = raw.results.find(
    (r) => r.operation === "tickets.withoutResponse" && Array.isArray(r.data)
  );
  const sla = raw.results.find(
    (r) => r.operation === "tickets.slaBreaches" && Array.isArray(r.data)
  );
  const avg = raw.results.find(
    (r) => r.operation === "tickets.averageResponseTime"
  );
  const byPeriod = raw.results.find(
    (r) => r.operation === "tickets.byPeriod" && Array.isArray(r.data)
  );

  if (count?.data?.count !== undefined) {
    blocos.metrics.push({
      label: "Total no período",
      value: formatarNumero(count.data.count),
      description: raw.period?.label || "",
      icon: BarChart3,
    });
  }
  if (avg?.data) {
    if (avg.data.avgFirstResponseMinutes != null) {
      blocos.metrics.push({
        label: "Tempo médio 1ª resposta",
        value: formatarDuracaoMin(avg.data.avgFirstResponseMinutes),
        description: `amostra: ${formatarNumero(
          avg.data.withResponseCount || 0
        )}`,
        icon: Clock,
      });
    }
    if (avg.data.avgResolutionMinutes != null) {
      blocos.metrics.push({
        label: "Tempo médio de resolução",
        value: formatarDuracaoMin(avg.data.avgResolutionMinutes),
        description: `amostra: ${formatarNumero(avg.data.closedCount || 0)}`,
        icon: CheckCircle2,
      });
    }
  }
  if (noResp?.data?.length) {
    blocos.metrics.push({
      label: "Sem resposta agora",
      value: formatarNumero(noResp.data.length),
      description: "tickets que aguardam retorno",
      icon: MessageCircle,
    });
  }
  if (sla?.data?.length) {
    blocos.metrics.push({
      label: "Fora do SLA",
      value: formatarNumero(sla.data.length),
      description: "acima do tempo acordado da fila",
      icon: AlertTriangle,
    });
  }

  if (byChannel?.data?.length) {
    blocos.tables.push({
      id: "tbl-channel",
      title: "Tickets por canal",
      columns: [
        { key: "channel", label: "Canal" },
        { key: "connectionName", label: "Conexão" },
        { key: "total", label: "Tickets" },
      ],
      rows: byChannel.data.map((x) => ({
        channel: x.channel || "desconhecido",
        connectionName: x.connectionName || "—",
        total: formatarNumero(x.total),
      })),
    });
    const total = byChannel.data.reduce(
      (acc, x) => acc + (x.total || 0),
      0
    );
    blocos.charts.push({
      id: "chart-channel",
      type: "donut",
      title: "Distribuição por canal",
      labels: byChannel.data.map((x) => x.channel || "desconhecido"),
      datasets: [
        {
          label: "Tickets",
          data: byChannel.data.map((x) => x.total || 0),
        },
      ],
    });
    if (total) {
      blocos.links.push({
        label: "Ver tickets por canal",
        href: "/tickets",
        icon: ArrowUpRight,
      });
    }
  }

  if (byAgent?.data?.length) {
    blocos.tables.push({
      id: "tbl-agent",
      title: "Tickets por atendente",
      columns: [
        { key: "userName", label: "Atendente" },
        { key: "total", label: "Tickets" },
      ],
      rows: byAgent.data.map((x) => ({
        userName: x.userName || "Sem atendente",
        total: formatarNumero(x.total),
      })),
    });
    blocos.charts.push({
      id: "chart-agent",
      type: "bar",
      title: "Tickets por atendente",
      labels: byAgent.data.map((x) => x.userName || "—"),
      datasets: [
        {
          label: "Tickets",
          data: byAgent.data.map((x) => x.total || 0),
        },
      ],
    });
  }

  if (byQueue?.data?.length) {
    blocos.tables.push({
      id: "tbl-queue",
      title: "Tickets por fila",
      columns: [
        { key: "queueName", label: "Fila" },
        { key: "total", label: "Tickets" },
      ],
      rows: byQueue.data.map((x) => ({
        queueName: x.queueName || "Sem fila",
        total: formatarNumero(x.total),
      })),
    });
    blocos.charts.push({
      id: "chart-queue",
      type: "bar",
      title: "Tickets por fila",
      labels: byQueue.data.map((x) => x.queueName || "—"),
      datasets: [
        {
          label: "Tickets",
          data: byQueue.data.map((x) => x.total || 0),
        },
      ],
    });
  }

  if (byPeriod?.data?.length) {
    blocos.charts.push({
      id: "chart-period",
      type: "line",
      title: "Evolução diária",
      labels: byPeriod.data.map((x) => x.day),
      datasets: [
        {
          label: "Tickets",
          data: byPeriod.data.map((x) => x.total || 0),
        },
      ],
    });
    blocos.tables.push({
      id: "tbl-period",
      title: "Evolução diária",
      columns: [
        { key: "day", label: "Dia" },
        { key: "total", label: "Tickets" },
      ],
      rows: byPeriod.data.map((x) => ({
        day: x.day,
        total: formatarNumero(x.total),
      })),
    });
  }

  if (noResp?.data?.length) {
    blocos.tables.push({
      id: "tbl-noresp",
      title: "Clientes sem resposta há mais tempo",
      columns: [
        { key: "ticketLink", label: "Ticket" },
        { key: "contactName", label: "Cliente" },
        { key: "queueName", label: "Fila" },
        { key: "userName", label: "Atendente" },
        { key: "hours", label: "Horas sem resposta" },
      ],
      rows: noResp.data.map((t) => ({
        ticketLink: {
          href: `/tickets/${t.id}`,
          label: `#${t.id}`,
        },
        contactName: t.contactName || "—",
        queueName: t.queueName || "—",
        userName: t.userName || "—",
        hours: `${(t.hoursWithoutResponse || 0).toFixed?.(1) || "0"} h`,
      })),
    });
  }

  if (sla?.data?.length) {
    blocos.tables.push({
      id: "tbl-sla",
      title: "Atendimentos fora do SLA",
      columns: [
        { key: "ticketLink", label: "Ticket" },
        { key: "contactName", label: "Cliente" },
        { key: "queueName", label: "Fila" },
        { key: "sla", label: "SLA (min)" },
        { key: "overBy", label: "Atraso (min)" },
      ],
      rows: sla.data.map((t) => ({
        ticketLink: {
          href: `/tickets/${t.id}`,
          label: `#${t.id}`,
        },
        contactName: t.contactName || "—",
        queueName: t.queueName || "—",
        sla: formatarNumero(t.slaMinutes || 0),
        overBy: formatarNumero(t.overByMinutes || 0),
      })),
    });
  }

  return blocos;
}

export default function Intelligence() {
  const { user: usuarioAuth, isAuth } = useContext(AuthContext);
  const navigate = useNavigate();
  const [pergunta, setPergunta] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [resposta, setResposta] = useState(null);
  const [erro, setErro] = useState(null);
  const [moduloDesativado, setModuloDesativado] = useState(false);
  const [empresaNome, setEmpresaNome] = useState("");
  const [empresaId, setEmpresaId] = useState(null);
  const [usuarioNome, setUsuarioNome] = useState("");
  const [paleta, setPaleta] = useState(() => ({
    primary: "#0b63e0",
    primaryClaro: ajustarLuminosidade("#0b63e0", 45),
    primaryMuitoClaro: ajustarLuminosidade("#0b63e0", 125),
    primaryEscuro: ajustarLuminosidade("#0b63e0", -35),
    accent: "#e0edff",
    graficos: ["#0b63e0", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#f97316", "#ec4899"],
  }));
  const [snapshot, setSnapshot] = useState(null);
  const [relInfo, setRelInfo] = useState(null);
  const textareaRef = useRef(null);
  const abortRef = useRef(null);
  const cacheRef = useRef(new Map());

  useEffect(() => {
    if (!isAuth || !usuarioAuth?.id) {
      // Nao autenticado: zera tudo. Nao le mais de localStorage que poderia
      // apontar para empresa/usuario da sessao anterior (bug reportado).
      setUsuarioNome("");
      setEmpresaNome("");
      setEmpresaId(null);
      setSnapshot(null);
      setRelInfo(null);
      try { cacheRef.current && cacheRef.current.clear(); } catch { /* noop */ }
      return;
    }

    setUsuarioNome(String(usuarioAuth.name || usuarioAuth.nome || ""));
    setEmpresaNome(String(usuarioAuth.company?.name || usuarioAuth.Company?.name || ""));
    const idRaw = usuarioAuth.companyId ?? usuarioAuth.company?.id ?? usuarioAuth.Company?.id ?? null;
    const id = idRaw === null || idRaw === undefined ? null : Number(idRaw);
    setEmpresaId(Number.isFinite(id) ? id : null);
  }, [
    isAuth,
    usuarioAuth?.id,
    usuarioAuth?.name,
    usuarioAuth?.companyId,
    usuarioAuth.company?.name,
    usuarioAuth.Company?.name,
  ]);

  useEffect(() => {
    if (!isAuth) return;

    const pal = criarPaletaDoSistema();
    setPaleta(pal);
    COLORS = pal.graficos;
    if (typeof document !== "undefined" && document.documentElement) {
      const r = document.documentElement;
      r.style.setProperty("--intel-primary", pal.primary);
      r.style.setProperty("--intel-primary-claro", pal.primaryClaro);
      r.style.setProperty("--intel-primary-muito-claro", pal.primaryMuitoClaro);
      r.style.setProperty("--intel-primary-escuro", pal.primaryEscuro);
      r.style.setProperty("--intel-primary-foreground", "#ffffff");
    }
    const carregarSnapshot = async () => {
      try {
        const { data } = await api.get("/api/intelligence/snapshot-hoje");
        setSnapshot(data?.snapshot || null);
      } catch {
        // silencioso: fallback para sugestões padrão
      }
    };
    carregarSnapshot();
  }, [isAuth, usuarioAuth?.id]);

  const montarSugestoesDinamicas = useCallback(() => {
    const snap = snapshot || {};
    const base = [...SUGESTOES_BASE];
    const dinamicas = [];
    const slaCount = Number(snap.slaBreachCount || 0);
    if (slaCount > 0) {
      dinamicas.push({
        rotulo: `${slaCount} fora do SLA agora`,
        pergunta: `Listar os ${slaCount} atendimentos que estão fora do SLA hoje.`,
        icone: AlertTriangle,
        tipo: "sla",
        destaque: true,
      });
    }
    const semRespCount = Number(snap.withoutResponseCount || 0);
    if (semRespCount > 0) {
      dinamicas.push({
        rotulo: `${semRespCount} sem resposta`,
        pergunta: `Quais são os ${semRespCount} clientes sem resposta neste momento?`,
        icone: MessageCircle,
        tipo: "semresposta",
        destaque: true,
      });
    }
    const topAgent = snap.topAgent;
    if (topAgent?.name) {
      dinamicas.push({
        rotulo: `${topAgent.name} (top atendente)`,
        pergunta: `Como está o desempenho dos atendentes hoje? Destaque para ${topAgent.name}.`,
        icone: Users,
        tipo: "atendentes",
      });
    }
    const topChannel = snap.topChannel;
    if (topChannel?.channel) {
      dinamicas.push({
        rotulo: `Canal ${topChannel.channel} em alta`,
        pergunta: `Qual a distribuição dos canais hoje, e porque ${topChannel.channel} está com mais atendimentos?`,
        icone: PhoneCall,
        tipo: "canais",
      });
    }
    const hojeCount = Number(snap.todayCount || 0);
    if (hojeCount >= 0) {
      dinamicas.push({
        rotulo: `${hojeCount} atendimentos hoje`,
        pergunta: `Quantos tickets foram abertos hoje e como está a comparação com ontem?`,
        icone: Search,
        tipo: "padrao",
      });
    }
    const unicas = new Map();
    [...dinamicas, ...base].forEach((s) => {
      if (!unicas.has(s.pergunta)) unicas.set(s.pergunta, s);
    });
    return Array.from(unicas.values()).slice(0, 10);
  }, [snapshot]);

  const perguntar = useCallback(
    async (texto) => {
      const clean = (texto || "").trim();
      if (!clean) return;

      if (abortRef.current) {
        try { abortRef.current.abort(); } catch { /* ignore */ }
        abortRef.current = null;
      }
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      let cachedValue = null;
      try {
        const chaveCache = `${empresaId || "x"}::${clean.toLowerCase()}`;
        const entry = cacheRef.current.get(chaveCache);
        if (entry && Date.now() - entry.ts < 15 * 60 * 1000) {
          cachedValue = entry.value;
        }
      } catch { /* ignore */ }

      try {
        setErro(null);
        setCarregando(true);
        setResposta(cachedValue);
        setPergunta(clean);
        if (cachedValue) {
          setRelInfo({
            numero: numeroRelatorio(cachedValue.period?.label),
            hash: hashStr6(
              `${clean}|${JSON.stringify(cachedValue.results || [])}|${Date.now()}`
            ),
          });
          setCarregando(false);
          return;
        }

        const { data } = await api.post(
          "/api/intelligence/ask",
          { question: clean },
          { signal: ctrl.signal }
        );
        const enriquecida = data;
        setResposta(enriquecida);
        setRelInfo({
          numero: numeroRelatorio(enriquecida.period?.label),
          hash: hashStr6(
            `${clean}|${JSON.stringify(enriquecida.results || [])}|${Date.now()}`
          ),
        });
        try {
          const chaveCache = `${empresaId || "x"}::${clean.toLowerCase()}`;
          cacheRef.current.set(chaveCache, { ts: Date.now(), value: enriquecida });
          if (cacheRef.current.size > 60) {
            const primeiraChave = cacheRef.current.keys().next().value;
            cacheRef.current.delete(primeiraChave);
          }
        } catch { /* ignore */ }
      } catch (err) {
        if (err?.code === "ERR_CANCELED" || err?.name === "AbortError" || err?.message === "canceled") {
          return;
        }
        console.error(err);
        const status = err?.response?.status;
        if (status === 403) {
          setModuloDesativado(true);
        }
        setErro(
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            "Não foi possível processar a pergunta. Tente novamente."
        );
        toast.error(
          err?.response?.data?.message ||
            "Erro ao consultar a Inteligência do Perabit."
        );
      } finally {
        setCarregando(false);
        if (abortRef.current === ctrl) abortRef.current = null;
      }
    },
    [empresaId]
  );

  const ui = resposta ? transformarRespostaParaUI(resposta) : null;

  const periodoInicio =
    resposta?.period?.startLabel ||
    formatarDataBr(resposta?.period?.start) ||
    "—";
  const periodoFim =
    resposta?.period?.endLabel ||
    formatarDataBr(resposta?.period?.end) ||
    "—";
  const dataRelatorio = formatarDataBr(new Date());
  const sugestoes = montarSugestoesDinamicas();

  const exportarPDF = useCallback(() => {
    if (!resposta) {
      toast.info("Faça uma pergunta primeiro para gerar o relatório.");
      return;
    }
    if (!relInfo) {
      setRelInfo({
        numero: numeroRelatorio(resposta.period?.label),
        hash: hashStr6(
          `${pergunta}|${JSON.stringify(resposta.results || [])}|${Date.now()}`
        ),
      });
    }
    setTimeout(() => window.print(), 60);
  }, [resposta, relInfo, pergunta]);

  const novaConsulta = useCallback(() => {
    setResposta(null);
    setErro(null);
    setRelInfo(null);
    setPergunta("");
    if (textareaRef.current) textareaRef.current.focus();
  }, []);

  const reutilizarPergunta = useCallback((texto) => {
    setPergunta(texto || "");
    if (textareaRef.current) {
      textareaRef.current.focus();
      try {
        const len = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(len, len);
      } catch { /* ignore */ }
    }
  }, []);

  return (
    <MainContainer className="intelligence-page">
      <MainHeader>
        <Title>
          <span className="inline-flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            Perabit Intelligence
          </span>
        </Title>
        <MainHeaderButtonsWrapper>
          {resposta ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={novaConsulta}
                className="items-center gap-1 print:hidden"
              >
                <RefreshCw className="h-4 w-4" />
                Nova consulta
              </Button>
              <Button
                size="sm"
                onClick={exportarPDF}
                disabled={!resposta}
                className="items-center gap-1 bg-primary text-primary-foreground hover:bg-primary/90 print:hidden"
              >
                <Download className="h-4 w-4" />
                Exportar PDF
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={exportarPDF}
                disabled={!resposta}
                className="items-center gap-1 print:hidden"
              >
                <Printer className="h-4 w-4" />
                Imprimir
              </Button>
            </>
          ) : null}
          {empresaNome ? (
            <Badge
              variant="outline"
              className="hidden border-slate-200 bg-slate-50 text-xs font-medium text-slate-700 sm:inline-flex dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 print:hidden"
            >
              {empresaNome}
            </Badge>
          ) : null}
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {/* ============ CABEÇALHO DO RELATÓRIO: SÓ APARECE NO PDF ============ */}
      {resposta ? (
        <div className="hidden print:block">
          {/* Variáveis para running headers/footers via @page CSS */}
          <div className="pdf__string-set">
            <span className="pdf__s-data">{dataRelatorio}</span>
            <span className="pdf__s-periodo">{resposta.period?.label}</span>
            <span className="pdf__s-empresa">{empresaNome || ""}</span>
          </div>

          <div className="mb-6 break-inside-avoid">
            <div className="flex items-start justify-between gap-6 border-b border-slate-300 pb-5">
              <div className="flex items-start gap-3">
                <div
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border text-white"
                  style={{
                    background: paleta.primary,
                    borderColor: paleta.primaryEscuro,
                  }}
                >
                  <FileText className="h-6 w-6" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight" style={{ color: paleta.primary }}>
                    Perabit Intelligence
                  </h1>
                  <p className="mt-1 text-sm text-slate-600">
                    Relatório gerado a partir de dados reais do CRM — READ ONLY.
                  </p>
                  {empresaNome ? (
                    <p className="mt-2 text-sm font-medium text-slate-900">
                      {empresaNome}
                    </p>
                  ) : null}
                  {relInfo?.numero ? (
                    <p className="mt-2 text-xs font-semibold tracking-wide" style={{ color: paleta.primary }}>
                      Relatório nº {relInfo.numero} · hash {relInfo.hash}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="w-full max-w-xs space-y-2 text-right text-sm text-slate-700">
                <div>
                  <span className="text-slate-500">Emitido em:&nbsp;</span>
                  <b className="font-semibold text-slate-900">{dataRelatorio}</b>
                </div>
                <div>
                  <span className="text-slate-500">Período:&nbsp;</span>
                  <b className="font-semibold text-slate-900">
                    {resposta.period?.label}
                  </b>
                </div>
                <div className="text-xs">
                  De <b>{periodoInicio}</b> até <b>{periodoFim}</b>
                </div>
                {usuarioNome ? (
                  <div className="text-xs">
                    <span className="text-slate-500">Solicitado por:&nbsp;</span>
                    <b className="font-medium text-slate-900">{usuarioNome}</b>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="flex-1 space-y-4 overflow-auto px-4 pb-4 sm:px-6 sm:pb-6 print:space-y-5 print:px-0 print:pb-0">
        {moduloDesativado ? (
          <Card className="border-amber-400/40 bg-amber-50/30 print:hidden dark:bg-amber-900/10">
            <CardContent className="flex items-start gap-3 pt-6">
              <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-500" />
              <div>
                <p className="font-medium">
                  O Perabit Intelligence está desativado.
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Solicite ao administrador da empresa que ative o módulo em
                  Configurações → Inteligência.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3 print:hidden"
                  onClick={() => navigate("/settings-intelligence")}
                >
                  Abrir configurações
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}

        <section className="print:hidden">
          {!resposta ? (
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Card className="border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                <CardContent className="flex items-start gap-3 pt-4 pb-4">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" style={{ background: "color-mix(in srgb, #ef4444 18%, transparent)", color: "#ef4444" }}>
                    <AlertTriangle className="h-4.5 w-4.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">
                      Acompanhe o SLA
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {snapshot?.slaBreachCount
                        ? `Há ${formatarNumero(snapshot.slaBreachCount)} atendimentos fora do SLA agora.`
                        : "Veja quais atendimentos estão atrasados e reaja rápido."}
                    </p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                <CardContent className="flex items-start gap-3 pt-4 pb-4">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" style={{ background: "color-mix(in srgb, #10b981 18%, transparent)", color: "#10b981" }}>
                    <Users className="h-4.5 w-4.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">
                      Performance da equipe
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {snapshot?.topAgent?.name
                        ? `${snapshot.topAgent.name} é o destaque de hoje.`
                        : "Veja quem mais resolve e os tempos médios de resposta."}
                    </p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                <CardContent className="flex items-start gap-3 pt-4 pb-4">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" style={{ background: "color-mix(in srgb, #f59e0b 18%, transparent)", color: "#b45309" }}>
                    <TrendingUp className="h-4.5 w-4.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">
                      Tendências diárias
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {snapshot?.todayCount
                        ? `Hoje já são ${formatarNumero(snapshot.todayCount)} atendimentos.`
                        : "Compare períodos e entenda os picos da semana."}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : null}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-[15px]">
                <Sparkles className="h-4 w-4 text-primary" />
                Pergunte ao Intelligence
              </CardTitle>
              <CardDescription>
                A pergunta é processada no backend com filtros de permissão e
                isolamento por empresa.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-3">
              <Textarea
                ref={textareaRef}
                value={pergunta}
                onChange={(e) => setPergunta(e.target.value)}
                placeholder='Ex: "Qual a performance da equipe esta semana?"'
                rows={3}
                className="resize-none text-base"
                disabled={carregando || moduloDesativado}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                    e.preventDefault();
                    perguntar(pergunta);
                  }
                }}
              />
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground">
                  Pressione ⌘ + Enter para enviar.
                </p>
                <Button
                  onClick={() => perguntar(pergunta)}
                  disabled={
                    carregando || !pergunta.trim() || moduloDesativado
                  }
                  className="items-center gap-1"
                >
                  {carregando ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Analisando…
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Perguntar
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="mt-4">
            <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <Lightbulb className="h-3.5 w-3.5" style={{ color: paleta.primary }} />
              Sugestões do momento
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {sugestoes.map((s) => {
                const I = s.icone;
                const destaque = s.destaque;
                return (
                  <Button
                    key={s.rotulo + s.pergunta}
                    variant={destaque ? "default" : "secondary"}
                    className={
                      "h-auto flex-col items-start gap-1.5 px-3 py-3 text-left " +
                      (destaque ? "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90" : "")
                    }
                    disabled={carregando || moduloDesativado}
                    onClick={() => {
                      setPergunta(s.pergunta);
                      perguntar(s.pergunta);
                    }}
                    style={
                      destaque
                        ? {
                            background: paleta.primary,
                            borderColor: paleta.primaryEscuro,
                          }
                        : undefined
                    }
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <I className={"h-4 w-4 " + (destaque ? "text-primary-foreground/90" : "text-muted-foreground")} />
                      {destaque ? <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-semibold">urgente</span> : null}
                    </div>
                    <div className="w-full">
                      <span className="text-sm font-medium">{s.rotulo}</span>
                      <p className={"line-clamp-2 text-[11px] leading-tight " + (destaque ? "text-white/80" : "text-muted-foreground")}>
                        {s.pergunta}
                      </p>
                    </div>
                  </Button>
                );
              })}
            </div>
          </div>
        </section>

        {carregando ? (
          <div className="space-y-5 print:hidden">
            <Skeleton className="h-8 w-2/3" />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-28 w-full rounded-lg" />
              ))}
            </div>
            <Skeleton className="h-80 w-full rounded-lg" />
            <Skeleton className="h-96 w-full rounded-lg" />
          </div>
        ) : null}

        {erro && !carregando ? (
          <Card className="border-red-400/40 print:hidden">
            <CardContent className="flex items-start gap-3 pt-6">
              <AlertTriangle className="mt-0.5 h-5 w-5 text-red-500" />
              <div>
                <p className="font-medium">Não foi possível responder.</p>
                <p className="mt-1 text-sm text-muted-foreground">{erro}</p>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {resposta && !carregando ? (
          <div className="space-y-4 print:space-y-5">
            {resposta.question ? (
              <Card className="print:hidden">
                <CardContent className="flex flex-wrap items-start gap-3 pt-5">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" style={{ background: paleta.primaryMuitoClaro, color: paleta.primary }}>
                    <Search className="h-4.5 w-4.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Pergunta
                    </p>
                    <p className="mt-1 text-sm font-medium text-foreground">
                      {resposta.question}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => reutilizarPergunta(resposta.question)}
                      className="gap-1"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      Editar
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => perguntar(resposta.question)}
                      className="gap-1"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      Atualizar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : null}

            {(resposta.slaCompliance != null || resposta.comparison != null) ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {resposta.slaCompliance != null ? (
                  <Card className="no-break">
                    <CardContent className="pt-3 pb-3">
                      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        Taxa de cumprimento do SLA
                      </p>
                      <div className="mt-1.5 flex items-end gap-2">
                        <p className="text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
                          {formatarNumero(Number(resposta.slaCompliance).toFixed(1))}%
                        </p>
                        {Number(resposta.slaCompliance) >= 85 ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800/40">
                            <CheckCircle2 className="mr-1 h-3 w-3" />
                            Excelente
                          </Badge>
                        ) : Number(resposta.slaCompliance) >= 60 ? (
                          <Badge className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800/40">
                            <AlertTriangle className="mr-1 h-3 w-3" />
                            Atenção
                          </Badge>
                        ) : (
                          <Badge className="bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800/40">
                            <AlertTriangle className="mr-1 h-3 w-3" />
                            Crítico
                          </Badge>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Considerando tickets com SLA configurado no período.
                      </p>
                    </CardContent>
                  </Card>
                ) : null}
                {resposta.comparison ? (
                  <Card className="no-break">
                    <CardContent className="pt-3 pb-3">
                      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        Comparativo vs período anterior
                      </p>
                      <div className="mt-1.5 flex items-end gap-2">
                        <p className="text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
                          {formatarNumero(resposta.comparison.totalPeriod || 0)}
                        </p>
                        <p className="text-xs text-muted-foreground pb-1">
                          de {formatarNumero(resposta.comparison.totalPrevious || 0)}
                        </p>
                        {Number(resposta.comparison.totalDeltaPercent || 0) > 0 ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800/40">
                            <TrendingUp className="mr-1 h-3 w-3" />
                            +{formatarNumero(Number(resposta.comparison.totalDeltaPercent).toFixed(1))}%
                          </Badge>
                        ) : Number(resposta.comparison.totalDeltaPercent || 0) < 0 ? (
                          <Badge className="bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800/40">
                            <TrendingUp className="mr-1 h-3 w-3 rotate-180" />
                            {formatarNumero(Number(resposta.comparison.totalDeltaPercent).toFixed(1))}%
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            Estável
                          </Badge>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Mesma janela de tempo, período imediatamente anterior.
                      </p>
                    </CardContent>
                  </Card>
                ) : null}
              </div>
            ) : null}

            <Card className="overflow-hidden">
              <CardHeader className="border-b pb-3 pt-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-[15px]">
                    Resumo executivo
                  </CardTitle>
                  {resposta.period?.label ? (
                    <CardDescription className="mt-1 text-sm">
                      <span className="text-slate-500">Período: </span>
                      <b className="text-foreground">
                        {resposta.period.label}
                      </b>{" "}
                      <span className="text-slate-500">— de </span>
                      <b className="text-foreground">{periodoInicio}</b>{" "}
                      <span className="text-slate-500">até </span>
                      <b className="text-foreground">{periodoFim}</b>
                    </CardDescription>
                  ) : null}
                </div>
                {resposta.queryInfo?.sources?.length ? (
                  <div className="flex flex-wrap justify-end gap-1 print:hidden">
                  <Badge variant="outline">
                    Fontes: {resposta.queryInfo.sources.join(", ")}
                  </Badge>
                </div>
              ) : null}
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-4 pb-4">
            {(() => {
              const temMetricas = ui?.metrics?.length > 0;
              const temTabelas = ui?.tables?.length > 0;
              const temGraficos = ui?.charts?.length > 0;
              const totalLinhas = resposta.queryInfo?.operations?.reduce((acc, op) => acc + (Number(op.rowCount) || 0), 0) || 0;
              const semDadosReal = !temMetricas && !temTabelas && !temGraficos && totalLinhas === 0;
              if (semDadosReal) {
                return (
                  <Card className="border-amber-400/40 bg-amber-50/30 mb-1 print:hidden dark:bg-amber-900/10">
                    <CardContent className="flex flex-wrap items-start gap-3 pt-4">
                      <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-500 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-amber-900 dark:text-amber-300">
                          Poucos ou nenhum dado encontrado neste período.
                        </p>
                        <p className="mt-1 text-sm text-amber-800/80 dark:text-amber-200/80">
                          Tente ampliar a janela de tempo ou mudar o foco da pergunta.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-amber-300 bg-white text-amber-800 hover:bg-amber-50 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800/40"
                            onClick={() => perguntar("Panorama de atendimentos dos últimos 7 dias.")}
                          >
                            Últimos 7 dias
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-amber-300 bg-white text-amber-800 hover:bg-amber-50 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800/40"
                            onClick={() => perguntar("Panorama de performance dos atendentes deste mês.")}
                          >
                            Panorama atendentes
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-amber-300 bg-white text-amber-800 hover:bg-amber-50 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800/40"
                            onClick={() => perguntar("Mostre a evolução dos atendimentos nos últimos 30 dias.")}
                          >
                            Evolução 30 dias
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              }
              return null;
            })()}

                <div className="rounded-xl border bg-slate-50/60 p-4 print:bg-white">
                  <p className="whitespace-pre-wrap text-base leading-relaxed text-foreground">
                    {resposta.answer}
                  </p>
                </div>
                {resposta.queryInfo?.operations?.length ? (
                  <div className="flex flex-wrap gap-2 print:hidden">
                    {resposta.queryInfo.operations.map((op, i) => (
                      <Badge key={i} variant="secondary">
                        {op.name} · {formatarNumero(op.rowCount || 0)} linhas
                      </Badge>
                    ))}
                  </div>
                ) : null}
              </CardContent>
            </Card>

            {ui.metrics?.length ? (
              <div className="no-break">
                <CardKPIS items={ui.metrics} />
              </div>
            ) : null}

            {ui.charts?.length ? (
              <div className="space-y-4 print:space-y-5">
                {ui.charts.map((g, i) => (
                  <Card key={i} className="no-break print:shadow-none">
                    <CardContent className="pt-5">
                      <Grfico
                        grafico={g}
                        id={g.id}
                        accent={
                          paleta.graficos[i % paleta.graficos.length] ||
                          paleta.primary
                        }
                      />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : null}

            {ui.tables?.length ? (
              <div className="space-y-4 print:space-y-5">
                {ui.tables.map((t, i) => (
                  <Card key={i} className="no-break print:shadow-none">
                    <CardContent className="pt-5">
                      <TabelaDados
                        tabela={t}
                        id={t.id}
                        title={t.title}
                        accent={
                          paleta.graficos[(i + 1) % paleta.graficos.length] ||
                          paleta.primary
                        }
                      />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : null}

          {ui.links?.length ? <Links links={ui.links} /> : null}

          {/* — — — Assinatura do relatório — — — */}
          <div
            className="mt-10 grid grid-cols-1 gap-8 divide-x-0 border-t pt-8 sm:grid-cols-3 sm:divide-x dark:divide-slate-700 dark:border-slate-700"
            style={{
              borderColor: "color-mix(in srgb, " + paleta.primary + " 24%, #e2e8f0)",
            }}
          >
            <div className="flex flex-col gap-2 sm:px-5 sm:first:pl-0">
              <p
                className="text-[11px] font-semibold uppercase tracking-[0.15em]"
                style={{ color: paleta.primary }}
              >
                Período
              </p>
              <p className="text-sm font-semibold text-slate-900 dark:text-white">
                {resposta.period?.label}
              </p>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                {periodoInicio} a {periodoFim}
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:px-5">
              <p
                className="text-[11px] font-semibold uppercase tracking-[0.15em]"
                style={{ color: paleta.primary }}
              >
                Solicitado por
              </p>
              <p className="text-sm font-semibold text-slate-900 dark:text-white">
                {usuarioNome || "Usuário autenticado"}
              </p>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Emitido em {dataRelatorio}
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:px-5 sm:last:pr-0">
              <p
                className="text-[11px] font-semibold uppercase tracking-[0.15em]"
                style={{ color: paleta.primary }}
              >
                Fonte dos dados
              </p>
              <p className="text-sm font-semibold text-slate-900 dark:text-white">
                Perabit CRM
              </p>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Todas as consultas executadas em modo READ ONLY.
              </p>
            </div>
          </div>
        </div>
      ) : null}
      </div>

      {/* — — — Estilos específicos do PDF (impressão) — — — */}
      <style>{`
        @media print {
          :root {
            color-scheme: light !important;
          }
          html, body {
            background: #ffffff !important;
            color: #0f172a !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
            font-size: 13px !important;
            line-height: 1.45 !important;
          }
          *, *::before, *::after {
            box-shadow: none !important;
          }
          .pdf__string-set { display: block !important; overflow: hidden; height: 0; width: 0; position: absolute; left: -9999px; pointer-events: none; }
          .pdf__s-data { string-set: pdfDataEmissao content(); }
          .pdf__s-periodo { string-set: pdfPeriodo content(); }
          .pdf__s-empresa { string-set: pdfEmpresa content(); }

          .print\\:hidden,
          .no-print-action,
          nav,
          aside,
          [data-slot="toast"],
          [role="dialog"],
          button,
          svg[data-state="open"],
          .Toastify,
          .toastify,
          #toast-container,
          [class*="sidebar"],
          [class*="TopBar"],
          [class*="top-bar"],
          div[role="presentation"],
          textarea, input, select, label, form {
            display: none !important;
          }
          .intelligence-page,
          .intelligence-page > *,
          .intelligence-page main,
          .intelligence-page > main,
          .intelligence-page > section {
            width: 100% !important;
            max-width: none !important;
            padding: 0 !important;
            margin: 0 !important;
            overflow: visible !important;
          }
          .no-break,
          table, thead, tbody, tr, th, td,
          [class*="rounded-"] {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
          table {
            font-size: 12px !important;
            border-collapse: collapse !important;
            width: 100% !important;
          }
          table th, table td {
            border: 1px solid #e2e8f0 !important;
            padding: 6px 8px !important;
          }
          thead { display: table-header-group !important; }
          tfoot { display: table-footer-group !important; }
          th {
            background: color-mix(in srgb, var(--intel-primary, #0b63e0) 14%, #ffffff) !important;
            color: var(--intel-primary, #0b63e0) !important;
            font-weight: 700 !important;
          }
          .Card,
          [class*="Card"]:not([class*="CardHeader"]):not([class*="CardContent"]):not([class*="CardTitle"]):not([class*="CardDescription"]):not([class*="CardFooter"]) {
            border: 1px solid #cbd5e1 !important;
            border-radius: 6px !important;
            margin: 0 0 14px 0 !important;
            padding: 0 !important;
            break-inside: avoid !important;
            background: #ffffff !important;
          }
          [class*="CardHeader"], [data-ui="card-header"] {
            padding: 10px 14px !important;
            border-bottom: 2px solid color-mix(in srgb, var(--intel-primary, #0b63e0) 30%, #e2e8f0) !important;
            background: color-mix(in srgb, var(--intel-primary, #0b63e0) 8%, #ffffff) !important;
          }
          [class*="CardTitle"] {
            font-size: 14px !important;
            font-weight: 700 !important;
            color: var(--intel-primary, #0b63e0) !important;
          }
          [class*="CardContent"], [data-ui="card-content"] {
            padding: 14px !important;
          }
          a { color: #0f172a !important; text-decoration: none !important; }
          [class*="Badge"] {
            display: inline-block;
            border: 1px solid color-mix(in srgb, var(--intel-primary, #0b63e0) 30%, #cbd5e1) !important;
            background: color-mix(in srgb, var(--intel-primary, #0b63e0) 10%, #ffffff) !important;
            color: var(--intel-primary, #0b63e0) !important;
            padding: 2px 8px !important;
            font-size: 11px !important;
            font-weight: 600 !important;
            border-radius: 9999px;
          }
          h1, h2, h3, h4 { color: var(--intel-primary, #0b63e0) !important; }
          p { orphans: 3; widows: 3; }
          section { margin-bottom: 12px; }
          .intel-card-kpi {
            border-left: 4px solid var(--intel-primary, #0b63e0) !important;
            box-shadow: 0 1px 2px rgba(15,23,42,0.06) !important;
          }
          @page :first {
            margin-top: 14mm;
            @top-left {
              content: "Perabit Intelligence";
              font-size: 11px;
              color: var(--intel-primary, #0b63e0);
              font-weight: 700;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            }
            @top-right {
              content: "Emitido em " string(pdfDataEmissao);
              font-size: 11px;
              color: var(--intel-primary, #0b63e0);
              font-weight: 600;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
              border-bottom: 2px solid color-mix(in srgb, var(--intel-primary, #0b63e0) 30%, #e2e8f0);
              padding-bottom: 2px;
            }
            @bottom-left {
              content: string(pdfEmpresa, first) " — Perabit CRM · READ ONLY";
              font-size: 10px;
              color: var(--intel-primary, #0b63e0);
              font-weight: 500;
            }
            @bottom-center {
              content: counter(page) " / " counter(pages);
              font-size: 10px;
              color: var(--intel-primary, #0b63e0);
              font-weight: 700;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            }
            @bottom-right {
              content: string(pdfPeriodo);
              font-size: 10px;
              color: var(--intel-primary, #0b63e0);
              font-weight: 600;
            }
          }
          @page {
            size: A4;
            margin: 20mm 16mm 22mm 16mm;
            @top-left {
              content: "Perabit Intelligence";
              font-size: 11px;
              color: var(--intel-primary, #0b63e0);
              font-weight: 700;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            }
            @top-right {
              content: "Emitido em " string(pdfDataEmissao);
              font-size: 11px;
              color: var(--intel-primary, #0b63e0);
              font-weight: 600;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            }
            @bottom-left {
              content: string(pdfEmpresa, first) " — Perabit CRM · READ ONLY";
              font-size: 10px;
              color: var(--intel-primary, #0b63e0);
              font-weight: 500;
            }
            @bottom-center {
              content: counter(page) " / " counter(pages);
              font-size: 10px;
              color: var(--intel-primary, #0b63e0);
              font-weight: 700;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            }
            @bottom-right {
              content: string(pdfPeriodo);
              font-size: 10px;
              color: var(--intel-primary, #0b63e0);
              font-weight: 600;
            }
          }
        }
      `}</style>
    </MainContainer>
  );
}
